import {DataStatus, Separator, Table} from 'platform/components';
import {Box, Hide, VStack} from 'platform/foundation';
import {useLocale} from 'platform/locale';

import {head, length} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {ScrollWithHiddenScrollbarInCypress} from '../../../../components/ScrollWithHiddenScrollbarInCypress/ScrollWithHiddenScrollbarInCypress';
import {useDrillDownQuery} from '../../../../store/omneticApi';
import {DrillDownLevelName, DrillDownStatisticsLevel} from '../../../../types/api/drillDown';
import {DrillDownLevelsConfigurator} from './components/DrillDownLevelsConfigurator';
import {DrillDownRow} from './components/DrillDownRow';
import {useDrillDownWithGlobalRowsMarketCounts} from './hooks/useDrillDownWithGlobalRowsMarketCounts';
import {useLevelConfigurationOrderQueryState} from './hooks/useLevelConfigurationOrderQueryState';
import {getKeyFromLevels} from './utils/getKeyFromLevels';
import {getLevelConfigurations} from './utils/getLevelConfigurations';
import {getPermanentLevelFilters} from './utils/getPermanentLevelFilters';
import {getTableColumns} from './utils/getTableColumns';

export interface DrillDownProps extends TestIdProps {
  currency: string;
  regions: string[];
  permanentFilters?: Partial<Record<DrillDownLevelName, string>>;
  hiddenLevels?: DrillDownLevelName[];
  selectedRow: Record<DrillDownLevelName, string | undefined>;
  scrollToSelectedRow?: boolean;
  boldFontForSelectedRowOnly?: boolean;
  hideConfigurator?: boolean;
  initialLevelOrder?: DrillDownLevelName[];
  isSimplified?: boolean;
  hasGlobalRowsWithMarketCounts?: boolean;
  minYear?: number;
  onSelect?: (levels: DrillDownStatisticsLevel[]) => void;
}

export function DrillDown(props: DrillDownProps) {
  const {language} = useLocale();
  const defaultLevelConfigurations = getLevelConfigurations(
    props.hiddenLevels,
    props.initialLevelOrder
  );
  const [levelConfigurations, setLevelConfigurations] = useLevelConfigurationOrderQueryState(
    defaultLevelConfigurations
  );

  const permanentLevelFilters = getPermanentLevelFilters(props.permanentFilters);
  const queryArg = {
    levels: [
      ...permanentLevelFilters,
      {
        level: head(levelConfigurations)?.name ?? 'make',
        values: [],
        priority: length(permanentLevelFilters) + 1,
      },
    ],
    currency: props.currency,
    lang: language,
  };

  const singleRegionResult = useDrillDownQuery(
    {...queryArg, regions: props.regions},
    {skip: props.hasGlobalRowsWithMarketCounts}
  );

  const globalRowsMarketCountsResult = useDrillDownWithGlobalRowsMarketCounts(
    {
      ...queryArg,
      regions: props.regions,
      minYear: props.minYear,
    },
    {skip: !props.hasGlobalRowsWithMarketCounts}
  );

  const result = props.hasGlobalRowsWithMarketCounts
    ? globalRowsMarketCountsResult
    : singleRegionResult;
  const {data, isLoading, isFetching, isError} = result;

  const columns = getTableColumns(props.currency, !!props.isSimplified);

  return (
    <VStack spacing={3}>
      <Hide when={props.hideConfigurator}>
        <DrillDownLevelsConfigurator
          value={levelConfigurations}
          onChange={setLevelConfigurations}
        />
      </Hide>
      <DataStatus isLoading={isLoading || isFetching} isError={isError} minHeight={50}>
        <ScrollWithHiddenScrollbarInCypress>
          <Box minWidth={!props.isSimplified ? 200 : undefined} paddingBottom={1}>
            <Hide when={props.hideConfigurator}>
              <Separator orientation="horizontal" spacing={0} />
            </Hide>
            <Box backgroundColor="general.white">
              <Table
                variant="stripped"
                columns={columns}
                data-testid={suffixTestId('table', props)}
              >
                {data?.statisticsAggregated.map((item) => (
                  <DrillDownRow
                    key={getKeyFromLevels(item.levels)}
                    depth={0}
                    data={item}
                    levelConfigurations={levelConfigurations}
                    currency={props.currency}
                    translations={data.translations}
                    regions={props.regions}
                    selectedRow={props.selectedRow}
                    scrollToSelectedRow={props.scrollToSelectedRow}
                    boldFontForSelectedRowOnly={props.boldFontForSelectedRowOnly}
                    isSimplified={props.isSimplified}
                    hasGlobalRowsWithMarketCounts={props.hasGlobalRowsWithMarketCounts}
                    minYear={props.minYear}
                    onSelect={props.onSelect}
                  />
                ))}
              </Table>
            </Box>
          </Box>
        </ScrollWithHiddenScrollbarInCypress>
      </DataStatus>
    </VStack>
  );
}
